export type BookingSlotMinutes = 15 | 30 | 60;

export const DEFAULT_BOOKING_SLOT_MINUTES: BookingSlotMinutes = 30;

export function isBookingSlotMinutes(value: unknown): value is BookingSlotMinutes {
  return value === 15 || value === 30 || value === 60;
}

/** Resolve slot size from API/stored JSON. Legacy `useOneHourBookingBlocks: true` maps to 60. */
export function resolveBookingSlotMinutes(
  data: { bookingSlotMinutes?: unknown; useOneHourBookingBlocks?: unknown } | null | undefined
): BookingSlotMinutes {
  const n = Number(data?.bookingSlotMinutes);
  if (isBookingSlotMinutes(n)) return n;
  if (data?.useOneHourBookingBlocks === true) return 60;
  return DEFAULT_BOOKING_SLOT_MINUTES;
}

/** Interval for generating raw slots. 1-hour mode still uses 30-minute underlying slots. */
export function rawSlotStepMinutes(slotMinutes: BookingSlotMinutes): 15 | 30 {
  return slotMinutes === 15 ? 15 : 30;
}

/** When one-hour UI is on, one button per clock hour (label HH:00) derived from underlying slots. */
export function buildHourDisplayTimeSlots(raw: string[], useOneHour: boolean): string[] {
  if (!useOneHour) return raw;
  const hourSet = new Set<number>();
  for (const t of raw) {
    const [h] = t.split(':').map(Number);
    if (!Number.isNaN(h)) hourSet.add(h);
  }
  return Array.from(hourSet)
    .sort((a, b) => a - b)
    .map((h) => `${String(h).padStart(2, '0')}:00`);
}

/** Parse "HH:mm", "H:mm", or "h:mm AM/PM" into minutes from midnight. */
export function parseClockTimeToMinutes(timeStr: string): number {
  const trimmed = String(timeStr || '').trim();
  const [time, period] = trimmed.split(/\s+/);
  const [hoursRaw, minutesRaw] = time.split(':').map(Number);
  let hour24 = hoursRaw;
  if (period) {
    const p = period.toUpperCase();
    if (p === 'PM' && hour24 !== 12) hour24 += 12;
    if (p === 'AM' && hour24 === 12) hour24 = 0;
  }
  return hour24 * 60 + (Number.isFinite(minutesRaw) ? minutesRaw : 0);
}

export function minutesToHhmm(totalMinutes: number): string | null {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0 || totalMinutes >= 24 * 60) return null;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Add ±3 hour override slots outside normal hours.
 *
 * Customer booking leaves core-hour gaps alone (the API omits starts that cannot
 * fit the selected duration). Business booking sets `fillCoreGaps` so owners
 * still see those times and can override.
 */
export function extendAvailabilityTimeSlots(
  apiSlotTimes: string[],
  slotStepMinutes: 15 | 30,
  coreRange: { startMinutes: number; endMinutes: number } | null,
  options?: { extendMinutes?: number; fillCoreGaps?: boolean }
): string[] {
  const extendMinutes = options?.extendMinutes ?? 180;
  const fillCoreGaps = options?.fillCoreGaps === true;
  const unique = Array.from(new Set(apiSlotTimes.filter((t): t is string => typeof t === 'string' && t.length > 0)));
  if (unique.length === 0) return unique;

  const parsed = unique.map(parseClockTimeToMinutes).sort((a, b) => a - b);
  const coreStart = coreRange?.startMinutes ?? parsed[0];
  const coreEnd = coreRange?.endMinutes ?? parsed[parsed.length - 1];
  const existing = new Set(unique);
  const extra: string[] = [];

  const extendedStart = Math.max(0, coreStart - extendMinutes);
  const extendedEnd = Math.min(24 * 60 - 1, coreEnd + extendMinutes);

  for (let minutes = extendedStart; minutes < coreStart; minutes += slotStepMinutes) {
    const timeStr = minutesToHhmm(minutes);
    if (timeStr && !existing.has(timeStr)) extra.push(timeStr);
  }

  if (fillCoreGaps) {
    for (let minutes = coreStart; minutes < coreEnd; minutes += slotStepMinutes) {
      const timeStr = minutesToHhmm(minutes);
      if (timeStr && !existing.has(timeStr)) extra.push(timeStr);
    }
  }

  // Only add slots at/after the real close — not after the last bookable start.
  for (let minutes = coreEnd; minutes < extendedEnd; minutes += slotStepMinutes) {
    const timeStr = minutesToHhmm(minutes);
    if (timeStr && !existing.has(timeStr)) extra.push(timeStr);
  }

  return Array.from(new Set([...unique, ...extra])).sort();
}

type AvailabilitySlotDisplay = {
  occupiedByExistingAppointment?: boolean;
  blockedByExistingAppointment?: boolean;
  availableProfessionals?: string[];
};

/**
 * Business booking: unavailable only when this 15/30-minute increment is
 * actually occupied. Starts that cannot fit the selected duration before a
 * later appointment stay selectable so owners can override.
 */
export function isOccupancyDisplaySlotAvailable(
  slot: AvailabilitySlotDisplay | undefined,
  professionalIds: string[]
): boolean {
  if (!slot) return true;
  if (slot.occupiedByExistingAppointment === true) return false;
  // Only treat duration lead-in as available when the API explicitly says this increment is not occupied.
  if (slot.occupiedByExistingAppointment === false && slot.blockedByExistingAppointment === true) {
    return true;
  }
  const available = slot.availableProfessionals || [];
  if (available.length === 0) return false;
  if (professionalIds.length === 0) return true;
  return professionalIds.some((id) => available.includes(id));
}

/**
 * Customer booking: a start is bookable only if the full selected duration
 * fits. Lead-in times before an existing appointment stay disabled.
 */
export function isCustomerDisplaySlotAvailable(
  slot: AvailabilitySlotDisplay | undefined,
  professionalIds: string[]
): boolean {
  if (!slot) return true;
  if (slot.occupiedByExistingAppointment === true) return false;
  if (slot.blockedByExistingAppointment === true) return false;
  const available = slot.availableProfessionals || [];
  if (available.length === 0) return false;
  if (professionalIds.length === 0) return true;
  return professionalIds.some((id) => available.includes(id));
}

/** @deprecated Use isOccupancyDisplaySlotAvailable */
export const isOwnerDisplaySlotAvailable = isOccupancyDisplaySlotAvailable;

export const DURATION_LEAD_IN_TOOLTIP =
  'Too little time window for selection service duration';

/** True when the increment is free but the selected duration would overlap a later booking. */
export function isDurationLeadInSlot(slot: AvailabilitySlotDisplay | undefined): boolean {
  if (!slot) return false;
  return slot.blockedByExistingAppointment === true && slot.occupiedByExistingAppointment !== true;
}

/** Cache key so switching staff does not reuse another professional's slot map. */
export function availabilityCacheKey(
  dateYmd: string,
  professionalIds: string[],
  durationMinutes: number,
  timeZone: string
): string {
  const ids = [...professionalIds].filter(Boolean).sort().join(',');
  return `${dateYmd}|${ids}|${Number(durationMinutes) || 0}|${timeZone}`;
}
